import React from "react";
import { View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  Icon,
  theme,
  Text,
  Heading,
  useResponsive,
} from "@clearabee/ui-library-native";
import { Button } from "components/Core/Button/Button";
import { ButtonLink } from "components/Core/ButtonLink/ButtonLink";
import { SKIP_PROHIBITED_ITEMS } from "lib/constants/prohibitedItems";

const displayProhibitedItems = "prohibited" as const;

const steps = [
  {
    step: "STEP 1",
    title: "Ordering your skip with us",
    contents: [
      "Have you considered what waste you’re getting rid of? If you’re clearing out a property, or removing furniture, household items or mixed waste, then our Man & Van service is often cheaper, quicker and more flexible than a skip.",
      "Consider that when ordering a skip we cannot accept these items:",
      displayProhibitedItems,
    ],
  },
  {
    step: "STEP 2",
    title: "Order Placed",
    contents: [
      "Once your order is placed, we’ll get to work scheduling your order for the day requested and size. If there’s any access details or questions our team will get in touch.",
    ],
  },
  {
    step: "STEP 3",
    title: "Delivery",
    contents: [
      "On the day of delivery, our transport team may get in touch if there's an issue delivering your skip, so please make sure the access is clear for your delivery and you're available via telephone.",
      "Deliveries can run through to the evening, and our team will contact you if there's a problem with your delivery, so please make sure the instructions for placement are clear, and the access is available. You won't need to get in touch with us.",
      "Typically your skip will be delivered by 4pm. We will be unable to give you an update on your delivery window before 4pm.",
    ],
  },
  {
    step: "STEP 4",
    title: "Filling your skip",
    contents: [
      "When filling your skip, please ensure that none of the following items are present:",
      displayProhibitedItems,
      "If any of these items are present, there will be additional charges or your skip may not be collected.",
    ],
  },
  {
    step: "STEP 5",
    title: "Collection",
    contents: [
      "When your skip is ready for collection, please let us know. When placing your order, we ask for a collection date, 3 to 14 days after delivery. Our typical maximum hire period is 14 days.",
      "We will endeavour to collect within 3 working days of the selected ready from date.",
    ],
  },
];

interface DisplaySkipHireInstructionsProps {
  onClickContinue: () => void;
}

export const DisplaySkipHireInstructions = ({
  onClickContinue,
}: DisplaySkipHireInstructionsProps): React.ReactElement => {
  const [translate] = useTranslation();
  const { screens } = useResponsive();

  return (
    <View
      style={{
        width: screens.large ? "60%" : "100%",
        height: "70vh",
        overflow: "scroll",
        margin: "auto",
      }}
    >
      {steps.map((step, index) => (
        <View
          key={"step-" + index}
          style={{
            backgroundColor: theme.colors.light.base,
            padding: theme.spacing.medium,
            borderRadius: theme.spacing.xsmall,
            marginBottom: theme.spacing.large,
          }}
        >
          <Text
            fontSize="xsmall"
            styles={{
              fontWeight: "500",
            }}
          >
            {step.step}
          </Text>
          <Heading
            level={4}
            color="brand"
            styles={{
              marginBottom: theme.spacing.small,
            }}
          >
            {step.title}
          </Heading>
          <View
            style={{
              marginBottom: theme.spacing.xsmall,
            }}
          >
            {step.contents.map((content, index) => {
              const isProhibitedItems = content === displayProhibitedItems;

              if (isProhibitedItems) {
                return (
                  <View
                    key={`prohibited-items-${index}`}
                    style={{
                      flexDirection: !screens.small ? "column" : "row",
                      flexWrap: "wrap",
                      marginBottom: theme.spacing.medium,
                      backgroundColor: theme.colors.greyscale.lightest,
                      padding: theme.spacing.small,
                      gap: theme.spacing.small,
                    }}
                  >
                    {(Object.keys(SKIP_PROHIBITED_ITEMS) as string[]).map(
                      (item) => (
                        <View
                          key={item}
                          style={{
                            flexDirection: "row",
                            alignItems: "center",
                            gap: theme.spacing.xsmall,
                          }}
                        >
                          <Icon.Close
                            width={10}
                            height={10}
                            color="light"
                            style={{
                              backgroundColor: theme.colors.negative.base,
                              padding: theme.spacing.xsmall2,
                              borderRadius: 50,
                            }}
                          />
                          <Text fontSize="xsmall">
                            {translate(
                              `hwa.screens.productDetails.prohibitedItems.skipList.${item}` as any,
                            )}
                          </Text>
                        </View>
                      ),
                    )}
                  </View>
                );
              }

              return (
                <Text
                  key={content}
                  fontSize="small"
                  styles={{
                    marginBottom: theme.spacing.medium,
                  }}
                >
                  {content}
                </Text>
              );
            })}
          </View>
        </View>
      ))}
      <View
        style={{
          flexDirection: !screens.small ? "column" : "row",
          justifyContent: !screens.large ? "center" : "flex-end",
          alignItems: "center",
          gap: theme.spacing.small,
        }}
      >
        <ButtonLink
          routeName="ServicePage"
          size="medium"
          color="accent"
          params={{
            categorySku: "MANVAN",
          }}
        >
          {translate("hwa.screens.skipHireInstructions.buttons.bookManVan")}
        </ButtonLink>
        <Button
          size="medium"
          color="warning"
          type="button"
          onClick={onClickContinue}
        >
          {translate("hwa.screens.skipHireInstructions.buttons.bookASkip")}
        </Button>
      </View>
    </View>
  );
};
